import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useMemo, useState } from 'react';
import { Search, Play, CheckCircle2, GraduationCap } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'academy:completed-lessons';
const STARTED_KEY = 'academy:started-lessons';
const TIMES_KEY = 'academy:lesson-times';
const CURRENT_KEY = 'academy:current-lesson';
// Percentual mínimo assistido para marcar automaticamente como concluída.
const COMPLETE_THRESHOLD = 0.9;

/**
 * Extrai a URL do `videoInformations.js` de dentro da URL de embed do player VSL.
 * Formato do embed:
 *   https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/{user}/{video}/videoInformations.js?...
 */
function getVideoInfoUrl(embedUrl: string): string | null {
  try {
    const match = embedUrl.match(/videoData=(https?:\/\/[^&]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Faz fetch do `videoInformations.js` (arquivo JS que declara `const objeto = {...}`)
 * e extrai a URL do MP4 via regex. Usar o MP4 direto evita que o Academy consuma
 * plays/cliques da conta que hospeda os vídeos.
 */
async function fetchMp4FromVideoInfo(videoInfoUrl: string): Promise<string | null> {
  try {
    const res = await fetch(videoInfoUrl);
    if (!res.ok) return null;
    const text = await res.text();
    const match = text.match(/"videoMP4"\s*:\s*"([^"]+)"/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

interface AcademyLesson {
  id: string;
  title: string;
  description: string;
  duration: string;
  videoUrl: string;
  embedUrl?: string;
  completed?: boolean;
}

interface AcademyModule {
  id: string;
  title: string;
  lessons: AcademyLesson[];
}

const V = 'https://www.w3schools.com/html/mov_bbb.mp4';

const MODULES: AcademyModule[] = [
  {
    id: 'm1',
    get title() { return translateInterfaceText("Módulo 1 Primeiros passos"); },
    lessons: [
      { id: 'l1', get title() { return translateInterfaceText("Visão geral da plataforma"); }, get description() { return translateInterfaceText("Conheça os pilares da Host VSL e entenda como cada área se conecta para acelerar sua operação."); }, duration: '4:32', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/50fd0d5b-2291-4bbf-a9ff-f3c27fbb11bb/videoInformations.js?VIDEO_ORIGIN=ORIGINAL', completed: true },
      { id: 'l2', get title() { return translateInterfaceText("Como subir seu primeiro vídeo"); }, get description() { return translateInterfaceText("Faça upload do seu primeiro vídeo e ajuste as configurações iniciais."); }, duration: '6:18', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/37b007af-efd6-4bbe-b48e-d5a9a255cf84/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l3', get title() { return translateInterfaceText("Domínios"); }, get description() { return translateInterfaceText("Configure domínios personalizados para seus vídeos."); }, duration: '5:00', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/8eb8eb03-5189-4a5d-8485-4f04de5f9920/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l28', get title() { return translateInterfaceText("Compartilhando vídeo"); }, get description() { return translateInterfaceText("Gere links de compartilhamento para que afiliados copiem seus vídeos rapidamente."); }, duration: '4:00', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/26aad259-03be-4018-b95e-585881c320c5/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
    ],
  },
  {
    id: 'm2',
    get title() { return translateInterfaceText("Módulo 2 Personalização"); },
    lessons: [
      { id: 'l4', get title() { return translateInterfaceText("Controles do player"); }, get description() { return translateInterfaceText("Configure os controles disponíveis para o espectador."); }, duration: '5:00', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/f3d9213b-ac01-4fea-a4b6-a2e9d52ef97a/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l5', get title() { return translateInterfaceText("Autoplay e Teste A/B"); }, get description() { return translateInterfaceText("Inicie o vídeo automaticamente com a melhor experiência."); }, duration: '4:20', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/7fdceac7-7374-44e1-8fd4-6f686766e5a9/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l6', get title() { return translateInterfaceText("Barra de progresso"); }, get description() { return translateInterfaceText("Personalize a barra de progresso do player."); }, duration: '3:50', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/91037fd0-2907-485c-a5d4-257ed97b656f/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l7', get title() { return translateInterfaceText("Continuar assistindo"); }, get description() { return translateInterfaceText("Permita que o espectador retome de onde parou."); }, duration: '4:05', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/bddc83db-094d-4984-8660-670726f4e8fd/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l8', get title() { return translateInterfaceText("Thumbnail"); }, get description() { return translateInterfaceText("Defina a capa ideal para seu vídeo."); }, duration: '3:30', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/72ec3e0f-f584-41d1-b352-74324c1c5ac8/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l9', get title() { return translateInterfaceText("Borda customizada"); }, get description() { return translateInterfaceText("Configure bordas personalizadas no player."); }, duration: '3:15', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/0e35be80-4b34-4171-834b-bb9b38e9c55c/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l10', get title() { return translateInterfaceText("Velocidade e Teste A/B"); }, get description() { return translateInterfaceText("Controle a velocidade de reprodução."); }, duration: '2:55', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/94c94b0c-02e1-428b-95c0-4711950a72d5/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l11', get title() { return translateInterfaceText("Exibição por dispositivo"); }, get description() { return translateInterfaceText("Defina o comportamento do player por aparelho."); }, duration: '4:10', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/1de4917c-7b19-4623-9424-b12c7fa415b5/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l12', get title() { return translateInterfaceText("Legendas"); }, get description() { return translateInterfaceText("Adicione legendas .VTT ou .SRT ao seu vídeo."); }, duration: '5:25', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/2a21e2f4-cdc9-4ed9-97c4-93a9e0a7c14e/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l16', get title() { return translateInterfaceText("Botão de ação"); }, get description() { return translateInterfaceText("Configure CTAs no momento certo do vídeo."); }, duration: '5:45', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/964ec993-9b2b-4db8-abbf-8e7c78c8cd20/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l17', get title() { return translateInterfaceText("Headline"); }, get description() { return translateInterfaceText("Adicione headlines persuasivas ao player."); }, duration: '4:30', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/8b11762e-048b-4a7c-b8e2-7f1c715a3ef3/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l19', get title() { return translateInterfaceText("Mini Hooks"); }, get description() { return translateInterfaceText("Use mini hooks para aumentar retenção."); }, duration: '4:50', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/d982cee6-5ebb-44f5-919a-b16ab47e6e78/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l29', get title() { return translateInterfaceText("Padronizar configurações"); }, get description() { return translateInterfaceText("Aplique configurações padrão em vários vídeos de uma só vez para acelerar seu fluxo."); }, duration: '4:30', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/faa62bcd-8f49-4fde-bf2d-76ecb775a642/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
    ],
  },
  {
    id: 'm3',
    get title() { return translateInterfaceText("Módulo 3 Recursos de conversão"); },
    lessons: [
      { id: 'l20', get title() { return translateInterfaceText("Captura de leads"); }, get description() { return translateInterfaceText("Configure formulários de captura integrados ao seu vídeo."); }, duration: '5:24', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/51a946d2-2754-4dfa-a169-3dc700d62344/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l21', get title() { return translateInterfaceText("Teste de hook"); }, get description() { return translateInterfaceText("Identifique a melhor introdução para seu vídeo."); }, duration: '8:11', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/9e298e48-5fbc-46ac-b95a-d5de7145313b/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l22', get title() { return translateInterfaceText("Cloaker"); }, get description() { return translateInterfaceText("Proteja suas campanhas e direcione tráfego com inteligência."); }, duration: '6:50', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/a8401fb8-8a7d-48aa-80e5-eaa9e8eb43ad/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l23', get title() { return translateInterfaceText("Teste A/B"); }, get description() { return translateInterfaceText("Compare versões e descubra o que mais converte."); }, duration: '5:36', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/ee41466d-5ca3-41c1-9f08-36d68fb8a66a/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
    ],
  },
  {
    id: 'm4',
    get title() { return translateInterfaceText("Módulo 4 Análises e performance"); },
    lessons: [
      { id: 'l24', get title() { return translateInterfaceText("Métricas"); }, get description() { return translateInterfaceText("Entenda os indicadores que importam e tome decisões baseadas em dados."); }, duration: '6:05', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/c27e3120-7570-4bc0-9abb-53190445c94c/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l25', get title() { return translateInterfaceText("Rastreamento"); }, get description() { return translateInterfaceText("Configure rastreamento avançado para acompanhar todo o funil."); }, duration: '4:48', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/145eb204-2061-4e8e-b370-3bdbdb3abc46/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
      { id: 'l27', get title() { return translateInterfaceText("Pixel"); }, get description() { return translateInterfaceText("Conecte pixels do Meta, Google e TikTok para rastrear conversões."); }, duration: '5:00', videoUrl: V, embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/fcc32ef5-271f-4769-9174-2875d477d3b8/videoInformations.js?VIDEO_ORIGIN=ORIGINAL' },
    ],
  },
  {
    id: 'm5',
    get title() { return translateInterfaceText("Módulo 5 Plano e plays"); },
    lessons: [
      {
        id: 'l26',
        get title() { return translateInterfaceText("Como funciona os plays extras"); },
        get description() { return translateInterfaceText("Entenda como funcionam os plays extras do seu plano e como utilizá-los a seu favor."); },
        duration: '5:00',
        videoUrl: V,
        embedUrl: 'https://script-prod.b-cdn.net/V0.600/?videoData=https://prod-hostvsl.b-cdn.net/bf740540-7398-4153-9db7-a41a6866c02d/93185f15-db1d-415d-ad91-6eb690cface0/videoInformations.js?VIDEO_ORIGIN=ORIGINAL',
      },
    ],
  },
];

const ACCENT = '#2563EB';

export default function Academy() {
  useLanguage();
  // Aula inicial: tenta restaurar a última que o usuário estava assistindo
  const [activeLessonId, setActiveLessonId] = useState<string>(() => {
    if (typeof window === 'undefined') return 'l1';
    try {
      const stored = window.localStorage.getItem(CURRENT_KEY);
      if (stored) return stored;
    } catch {}
    return 'l1';
  });
  const [search, setSearch] = useState('');

  const [completedIds, setCompletedIds] = useState<Set<string>>(() => {
    const defaults = MODULES.flatMap(m => m.lessons.filter(l => l.completed).map(l => l.id));
    if (typeof window === 'undefined') return new Set(defaults);
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) return new Set(JSON.parse(stored) as string[]);
    } catch {}
    return new Set(defaults);
  });

  const [startedIds, setStartedIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set(['l1', 'l2']);
    try {
      const stored = window.localStorage.getItem(STARTED_KEY);
      if (stored) return new Set(JSON.parse(stored) as string[]);
    } catch {}
    return new Set(['l1', 'l2']);
  });

  // Tempo assistido por aula (em segundos). Alimentado pelo onTimeUpdate do
  // <video> nativo.
  const [lessonTimes, setLessonTimes] = useState<Record<string, number>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = window.localStorage.getItem(TIMES_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  });

  // Cache de URLs MP4 resolvidas a partir do videoInformations.js de cada aula.
  // Evita fazer fetch duplicado ao voltar para a mesma aula.
  const [mp4UrlByLesson, setMp4UrlByLesson] = useState<Record<string, string>>({});

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(completedIds)));
    } catch {}
  }, [completedIds]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STARTED_KEY, JSON.stringify(Array.from(startedIds)));
    } catch {}
  }, [startedIds]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TIMES_KEY, JSON.stringify(lessonTimes));
    } catch {}
  }, [lessonTimes]);

  // Persiste a última aula assistida para restaurar na próxima visita.
  useEffect(() => {
    try {
      window.localStorage.setItem(CURRENT_KEY, activeLessonId);
    } catch {}
  }, [activeLessonId]);

  // Marca aula como iniciada ao selecioná-la.
  useEffect(() => {
    setStartedIds(prev => {
      if (prev.has(activeLessonId)) return prev;
      const next = new Set(prev);
      next.add(activeLessonId);
      return next;
    });
  }, [activeLessonId]);

  // Resolve a URL do MP4 a partir do videoInformations.js da aula ativa.
  // Só faz fetch se ainda não tiver cache para aquela aula.
  useEffect(() => {
    const lesson = MODULES.flatMap(m => m.lessons).find(l => l.id === activeLessonId);
    if (!lesson?.embedUrl) return;
    if (mp4UrlByLesson[activeLessonId]) return; // cache hit

    let cancelled = false;
    const videoInfoUrl = getVideoInfoUrl(lesson.embedUrl);
    if (!videoInfoUrl) return;

    fetchMp4FromVideoInfo(videoInfoUrl).then(url => {
      if (cancelled || !url) return;
      setMp4UrlByLesson(prev => ({ ...prev, [activeLessonId]: url }));
    });

    return () => {
      cancelled = true;
    };
  }, [activeLessonId, mp4UrlByLesson]);


  const allLessons = useMemo(
    () => MODULES.flatMap(m => m.lessons.map(l => ({ ...l, moduleTitle: m.title }))),
    []
  );
  const activeLesson = allLessons.find(l => l.id === activeLessonId) ?? allLessons[0];
  const totalLessons = allLessons.length;
  // Conta apenas aulas concluídas que AINDA EXISTEM na listagem. Evita
  // inconsistência quando o localStorage guarda IDs de aulas removidas.
  const completedCount = useMemo(() => {
    const existing = new Set(allLessons.map(l => l.id));
    let n = 0;
    completedIds.forEach(id => {
      if (existing.has(id)) n++;
    });
    return n;
  }, [completedIds, allLessons]);
  const progressPct = totalLessons ? Math.round((completedCount / totalLessons) * 100) : 0;

  const handleVideoEnded = () => {
    setCompletedIds(prev => {
      if (prev.has(activeLessonId)) return prev;
      const next = new Set(prev);
      next.add(activeLessonId);
      return next;
    });
  };

  // Fallback para <video> nativo: rastreia progresso e marca como concluída
  // ao atingir 90% — mesma regra do iframe via postMessage.
  const handleVideoTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (!v.duration || v.duration <= 0) return;
    setLessonTimes(prev => {
      const last = prev[activeLessonId] || 0;
      if (Math.abs(v.currentTime - last) < 1) return prev;
      return { ...prev, [activeLessonId]: v.currentTime };
    });
    if (v.currentTime >= v.duration * COMPLETE_THRESHOLD) {
      setCompletedIds(prev => {
        if (prev.has(activeLessonId)) return prev;
        const next = new Set(prev);
        next.add(activeLessonId);
        return next;
      });
    }
  };

  const filteredModules = useMemo(() => {
    if (!search.trim()) return MODULES;
    const q = search.toLowerCase();
    return MODULES
      .map(m => ({ ...m, lessons: m.lessons.filter(l => l.title.toLowerCase().includes(q)) }))
      .filter(m => m.lessons.length > 0);
  }, [search]);

  return (
    <Layout fixedSidebar>
      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFF 100%)' }}
      >
        {/* Header — padrão MetricsDashboard */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            <div className="flex items-center gap-2.5">
              <GraduationCap className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
              <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
                {translateInterfaceText("Academy")}</h1>
            </div>
          </div>
          {/* Linha divisória full-width (até o menu lateral) — somente desktop */}
          <div
            className="hidden lg:block h-px w-full"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
          />
        </div>

        {/* Conteúdo principal — página fixa, só o aside scrolla.
            Grid CSS faz todo o trabalho de layout sem JS:
            • Mobile:  1 coluna, linhas [auto, 1fr] → aside toma o espaço restante
            • Desktop: 2 colunas [1fr 360px], 1 linha → aside usa self-stretch
                       pra acompanhar a altura do player automaticamente
            Em ambos os casos, o aside tem overflow-hidden + filho com
            overflow-y-auto para o scroll acontecer SOMENTE na lista de aulas. */}
        <main className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full max-w-[1440px] w-full mx-auto px-4 md:px-8 lg:px-10 py-6">
            <div className="h-full grid gap-6 grid-rows-[auto_1fr] grid-cols-1 lg:grid-rows-[auto] lg:grid-cols-[1fr_360px]">
              {/* Player — usa MP4 direto do CDN (sem player VSL embedado) para
                  não consumir plays/cliques das contas que hospedam as aulas.
                  A URL é resolvida via fetch do videoInformations.js. Enquanto
                  não chega, mostramos um placeholder preto para não exibir
                  vídeo de exemplo. */}
              <div className="w-full">
                <div className="rounded-[16px] overflow-hidden aspect-video bg-black">
                  {mp4UrlByLesson[activeLesson.id] ? (
                    <video
                      key={activeLesson.id}
                      src={mp4UrlByLesson[activeLesson.id]}
                      controls
                      controlsList="nodownload"
                      playsInline
                      onEnded={handleVideoEnded}
                      onTimeUpdate={handleVideoTimeUpdate}
                      className="w-full h-full object-contain bg-black"
                    />
                  ) : (
                    <div className="w-full h-full bg-black" />
                  )}
                </div>
              </div>

              {/* Aside — lista de aulas com altura fixa + scroll interno.
                  Usa calc(100vh - X) para garantir que sempre caiba na viewport:
                  Mobile: desconta mobile header (~52px) + header da página
                          (~52px) + padding (~48px) + player em aspect-video
                          (~200px) + gap + folga.
                  Desktop: sem mobile header e sem player acima → mais altura. */}
              <aside
                className="bg-white border border-[#D9DEE6] rounded-[16px] overflow-hidden flex flex-col h-[calc(100vh-380px)] min-h-[320px] lg:h-[calc(100vh-160px)] lg:min-h-0"
              >
                {/* Search */}
                <div className="px-5 pt-4 pb-3 border-b border-[#EEF0F3] flex-shrink-0">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder={translateInterfaceText("Buscar aula...")}
                      className="w-full h-10 pl-9 pr-3 rounded-[10px] border border-[#E4E7EC] bg-white text-[13.5px] text-[#0F172A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15 transition"
                    />
                  </div>
                </div>

                {/* Progresso */}
                <div className="px-5 py-4 border-b border-[#EEF0F3] flex-shrink-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11.5px] font-medium text-[#6B7280]">
                      {completedCount} {" "}{translateInterfaceText("de")}{" "}{totalLessons} {" "}{translateInterfaceText("aulas concluídas")}</span>
                    <span className="text-[11.5px] font-semibold text-[#0F172A]">{progressPct}%</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-[#EEF0F3] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${progressPct}%`, background: ACCENT }}
                    />
                  </div>
                </div>

                {/* Lista de aulas — SCROLL INTERNO */}
                <div className="flex-1 min-h-0 overflow-y-auto sidebar-scrollbar pb-3">
                  {filteredModules.length === 0 ? (
                    <div className="px-5 py-8 text-center text-[13px] text-[#6B7280]">
                      {translateInterfaceText("Nenhuma aula encontrada.")}</div>
                  ) : (
                    filteredModules.map(module => (
                      <div key={module.id} className="border-b border-[#F1F3F6] last:border-b-0">
                        <div className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                          {module.title}
                        </div>
                        <div className="pb-2 px-2">
                          {module.lessons.map(lesson => {
                            const isActive = lesson.id === activeLessonId;
                            const isCompleted = completedIds.has(lesson.id);
                            const isStarted = startedIds.has(lesson.id) || isCompleted;
                            const activeIdx = allLessons.findIndex(l => l.id === activeLessonId);
                            const nextLessonId = allLessons[activeIdx + 1]?.id;
                            const isNext = lesson.id === nextLessonId;

                            return (
                              <button
                                key={lesson.id}
                                onClick={() => setActiveLessonId(lesson.id)}
                                className={cn(
                                  'group/lesson w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors duration-150 ease-out relative cursor-pointer rounded-[10px]',
                                  isActive ? 'bg-[#F4F8FF]' : 'bg-transparent hover:bg-[#EEF0F3]'
                                )}
                              >
                                {isActive && (
                                  <span
                                    className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full"
                                    style={{ background: ACCENT }}
                                  />
                                )}
                                <div
                                  className={cn(
                                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                                    isActive
                                      ? 'text-white'
                                      : isCompleted
                                        ? 'bg-[#EAF1FF]'
                                        : 'bg-[#F3F4F6]'
                                  )}
                                  style={isActive ? { background: ACCENT } : undefined}
                                >
                                  {isCompleted && !isActive ? (
                                    <CheckCircle2 className="w-4 h-4" style={{ color: ACCENT }} />
                                  ) : (
                                    <Play
                                      className="w-3.5 h-3.5"
                                      style={
                                        isActive
                                          ? { color: '#ffffff', fill: '#ffffff' }
                                          : isStarted
                                            ? { color: ACCENT }
                                            : { color: '#6B7280' }
                                      }
                                    />
                                  )}
                                </div>
                                <div
                                  title={lesson.title}
                                  className={cn(
                                    'flex-1 min-w-0 text-[13.5px] truncate',
                                    isActive ? 'font-semibold text-[#0F172A]' : 'font-medium text-[#374151]'
                                  )}
                                >
                                  {lesson.title}
                                </div>
                                {isNext && (
                                  <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#EEF4FF] text-[#2563EB] border border-[#DCE7FF]">
                                    {translateInterfaceText("Próxima aula")}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </aside>
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
}
